const CaseStudies = () => {
  return (
    <div>
      <div className="max-w-4xl mx-auto mb-12">
        <h1 className="text-4xl font-bold mb-6">Case Studies</h1>
        <p className="text-lg text-glam-secondary dark:text-gray-300">
          Explore how we've helped cultural institutions transform their collections 
          into powerful digital resources.
        </p>
      </div>

      {/* Pinball Publishing Case Study */}
      <article className="max-w-6xl mx-auto mb-16">
        <div className="grid lg:grid-cols-3 gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 text-sm text-glam-accent mb-4">
              <span>Digital Archive</span>
              <span>•</span>
              <span>Knowledge Platform</span>
              <span>•</span>
              <span>AI-Powered Search</span>
            </div>
            
            <h2 className="text-3xl font-bold mb-4">
              Pinball Publishing: Preserving New York City's History
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8 py-6 border-y border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="font-semibold mb-1">Client</h3>
                <p className="text-glam-secondary dark:text-gray-300">Pinball Publishing</p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Industry</h3>
                <p className="text-glam-secondary dark:text-gray-300">Publishing & Archives</p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Timeline</h3>
                <p className="text-glam-secondary dark:text-gray-300">6 months</p>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-1 relative">
            <div className="aspect-square relative">
              {/* First screenshot - back left */}
              <img
                src="/images/pinball-screenshot-1.png"
                alt="Pinball Publishing AI-powered NYC history explorer interface showing historical documents and search functionality"
                className="absolute -top-4 -left-4 w-4/5 h-4/5 object-cover rounded-[3rem] shadow-lg z-10"
              />
              {/* Second screenshot - front right */}
              <img
                src="/images/pinball-screenshot-2.png"
                alt="Pinball Publishing search results and document details view"
                className="absolute bottom-0 right-0 w-4/5 h-4/5 object-cover rounded-[3rem] shadow-lg z-20"
              />
            </div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <section className="mb-8">
            <h3 className="text-xl font-semibold mb-4">The Challenge</h3>
            <p className="text-glam-secondary dark:text-gray-300 mb-4">
              Pinball Publishing had amassed an extensive collection of historical documents, 
              photographs, maps, and ephemera related to New York City's rich history. Spanning 
              from the colonial era to the present day, their archive contained thousands of 
              documents that had been carefully scanned and digitized into PDFs.
            </p>
            <p className="text-glam-secondary dark:text-gray-300">
              The collection included rare manuscripts, vintage photographs, and historical maps 
              that together told the story of America's most iconic city. Pinball had done the 
              meticulous work of connecting each document to specific addresses mentioned within them, 
              creating a unique geographical view of New York City's history. However, they needed 
              a web application to make this remarkable collection accessible to researchers and 
              the public, transforming their digitized archive into an interactive platform.
            </p>
          </section>

          <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Our Solution</h3>
              <p className="text-glam-secondary dark:text-gray-300 mb-4">
                We designed and implemented a comprehensive digital platform that transformed 
                Pinball Publishing's archive into a searchable, interactive, AI-powered explorer:
              </p>
              <ul className="space-y-3 text-glam-secondary dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-glam-accent mr-3">•</span>
                  <span><strong>Intelligent Digitization:</strong> High-resolution scanning with OCR 
                  and metadata extraction for all document types</span>
                </li>
                <li className="flex items-start">
                  <span className="text-glam-accent mr-3">•</span>
                  <span><strong>AI-Powered Organization:</strong> Automatic categorization by era, 
                  neighborhood, topic, and historical significance</span>
                </li>
                <li className="flex items-start">
                  <span className="text-glam-accent mr-3">•</span>
                  <span><strong>Advanced Search:</strong> Natural language queries to find documents 
                  by content, context, or connections</span>
                </li>
                <li className="flex items-start">
                  <span className="text-glam-accent mr-3">•</span>
                  <span><strong>Interactive Timeline:</strong> Visual exploration of NYC history 
                  through chronologically organized materials</span>
                </li>
                <li className="flex items-start">
                  <span className="text-glam-accent mr-3">•</span>
                  <span><strong>Public Engagement Portal:</strong> User-friendly interface for 
                  educators, researchers, and history enthusiasts</span>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4">The Results</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
                  <div className="text-3xl font-bold text-glam-accent mb-2">
                    20,000+
                  </div>
                  <p className="text-glam-secondary dark:text-gray-300">
                    New York Locations
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
                  <div className="text-3xl font-bold text-glam-accent mb-2">
                    200,000+
                  </div>
                  <p className="text-glam-secondary dark:text-gray-300">
                    Document Extracts
                  </p>
                </div>
              </div>
            </section>
        </div>
      </article>

      {/* More Case Studies Coming Soon */}
      <section className="max-w-4xl mx-auto">
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-12 text-center">
          <h3 className="text-2xl font-bold mb-4">Be The Next Success Story</h3>
          <p className="text-glam-secondary dark:text-gray-300 mb-6">
            We're currently working on exciting projects with museums, libraries, and archives 
            worldwide. Check back soon for more success stories.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-glam-primary text-white font-medium hover:bg-glam-secondary transition-colors"
          >
            Start Your Project
          </a>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;